import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import PastorModeView from './games/PastorModeView';
import { Button } from './ui/button';
import { IceCream, ArrowRight } from 'lucide-react';

/**
 * PUBLIC Pastor-Mode page at `/pastor?ids=u1,u2,u3`.
 *
 * This route is intentionally OUTSIDE <ProtectedRoute> so a pastor can
 * paste the URL into a church bulletin / WhatsApp / email and members
 * (logged-in or not) can see the 7-day group leaderboard without an auth
 * wall. The backend endpoint /api/games/sundae/group-week is also public,
 * so the data fetch works for unauthenticated viewers.
 *
 * Authenticated viewers see a "Run today's sundae" CTA at the bottom that
 * deep-links into the regular flow at /community?tab=games&sundae=start.
 */
const PastorModePublicPage = () => {
  const [searchParams] = useSearchParams();
  const { language } = useLanguage();
  const idsParam = searchParams.get('ids') || searchParams.get('pastor') || '';
  const userIds = idsParam.split(',').map((s) => s.trim()).filter(Boolean);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 dark:from-slate-950 dark:to-purple-950/30 py-6 sm:py-10">
      <div className="max-w-3xl mx-auto px-4 space-y-6">
        <div className="text-center space-y-2">
          <div className="inline-flex items-center gap-2 text-purple-700 dark:text-purple-300 font-bold text-sm uppercase tracking-widest">
            <IceCream className="w-4 h-4" />
            Lumera · Pastor Mode
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-slate-900 dark:text-white">
            {language === 'es'
              ? 'Tabla del Sundae · 7 días'
              : '7-Day Sundae Leaderboard'}
          </h1>
          <p className="text-sm text-muted-foreground max-w-xl mx-auto">
            {language === 'es'
              ? 'Vista pública para pastores y líderes de grupo. Comparte este enlace con tu grupo para ver quién jugó cada día.'
              : 'Public view for pastors and small-group leaders. Share this link with your group to see who played each day.'}
          </p>
        </div>

        {userIds.length === 0 ? (
          <div className="rounded-lg border border-dashed border-purple-300 dark:border-purple-700 p-6 text-center text-sm text-muted-foreground">
            {language === 'es'
              ? 'Falta el parámetro ?ids=user1,user2 en la URL.'
              : 'Missing ?ids=user1,user2 URL parameter.'}
          </div>
        ) : (
          <PastorModeView language={language} userIds={userIds} />
        )}

        <div className="text-center pt-2">
          <Button
            asChild
            variant="ghost"
            className="text-purple-700 dark:text-purple-300"
            data-testid="pastor-public-cta"
          >
            <a href="/community?tab=games&sundae=start">
              {language === 'es' ? 'Jugar el Sundae de hoy' : 'Play today\u2019s Sundae'}
              <ArrowRight className="w-4 h-4 ml-1" />
            </a>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PastorModePublicPage;
